import React, { useMemo, useState } from 'react';
import { updateLegalBoardTerm } from './api';
import { DEFAULT_ROLE_SUGGESTIONS } from './constants';
import LegalBoardCompositionEditor, { errorMessages, termMembersToLocal } from './LegalBoardCompositionEditor';
import { compositionHasChanged, membersForBoardChange, membersToPayload, validateCompositionClient } from './legalBoardUi';

/**
 * Flat form to fix the current board (a typo, a wrong date, a missing role)
 * without going through the wizard. Every change is recorded in the history
 * by the backend (`term_corrected`).
 */
function LegalBoardCorrection({ term, overview, userId, castellersInfo, onReload, onClose, onAnnul, onPrepareChange }) {
    const roleSuggestions = overview?.roleSuggestions || DEFAULT_ROLE_SUGGESTIONS;
    const [dates, setDates] = useState({
        mandateStart: term.mandateStart || '',
        mandateEnd: term.mandateEnd || '',
        nextReviewDate: term.nextReviewDate || '',
    });
    const [members, setMembers] = useState(() => termMembersToLocal(term.members));
    const [exceptionNote, setExceptionNote] = useState(term.compositionException || '');
    const [compositionCorrection, setCompositionCorrection] = useState(false);
    const compositionChanged = compositionHasChanged(term.members, members);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [dateErrors, setDateErrors] = useState([]);
    const [serverErrors, setServerErrors] = useState([]);

    const validation = useMemo(() => validateCompositionClient(members, { exceptionNote }), [members, exceptionNote]);

    const save = async () => {
        if (busy) return;
        setError(null);
        setDateErrors([]);
        setServerErrors([]);
        if (!validation.valid) {
            setServerErrors(errorMessages(validation.errors));
            return;
        }
        if (compositionChanged && !compositionCorrection) return;
        setBusy(true);
        try {
            await updateLegalBoardTerm({
                userId,
                termId: term.id,
                payload: { ...dates, members: membersToPayload(members), compositionException: exceptionNote, compositionCorrection },
            });
            await onReload();
            onClose({ message: { tone: 'success', text: 'Correcció desada i anotada a l\'historial.' } });
        } catch (saveError) {
            const details = saveError?.payload?.details;
            const dateMessages = errorMessages(details?.dates?.errors);
            const compositionMessages = errorMessages(details?.composition?.errors);
            if (dateMessages.length > 0) setDateErrors(dateMessages);
            if (compositionMessages.length > 0) setServerErrors(compositionMessages);
            if (dateMessages.length === 0 && compositionMessages.length === 0) {
                setError(saveError.message || 'No s\'ha pogut desar la correcció.');
            }
        } finally {
            setBusy(false);
        }
    };

    const prepareChange = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await onPrepareChange({ members: membersToPayload(membersForBoardChange(term.members, members)), compositionException: exceptionNote });
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="legal-board-card legal-board-correction" aria-labelledby="legal-board-correction-title">
            <header className="legal-board-card-head">
                <div>
                    <h3 id="legal-board-correction-title">Corregir la junta actual</h3>
                    <p className="legal-board-muted legal-board-small">Per corregir dades mal introduïdes. Si la colla ha canviat persones o càrrecs, prepara un canvi de junta. Els canvis queden anotats a l&apos;historial.</p>
                </div>
                <button type="button" className="legal-board-button link" onClick={() => onClose()} disabled={busy}>Tancar</button>
            </header>
            {error ? <div className="legal-board-alert error" role="alert">{error}</div> : null}

            <div className="legal-board-form-grid three">
                <div className="legal-board-field">
                    <label htmlFor="legal-board-correction-start">Vigent des de</label>
                    <input id="legal-board-correction-start" type="date" value={dates.mandateStart} onChange={(event) => setDates((value) => ({ ...value, mandateStart: event.target.value }))} disabled={busy} required />
                </div>
                <div className="legal-board-field">
                    <label htmlFor="legal-board-correction-end">Final previst (opcional)</label>
                    <input id="legal-board-correction-end" type="date" value={dates.mandateEnd} onChange={(event) => setDates((value) => ({ ...value, mandateEnd: event.target.value }))} disabled={busy} />
                </div>
                <div className="legal-board-field">
                    <label htmlFor="legal-board-correction-review">Pròxima revisió</label>
                    <input id="legal-board-correction-review" type="date" value={dates.nextReviewDate} onChange={(event) => setDates((value) => ({ ...value, nextReviewDate: event.target.value }))} disabled={busy} required />
                </div>
            </div>
            {dateErrors.length > 0 ? <p className="legal-board-field-error" role="alert">{dateErrors.join(' · ')}</p> : null}

            <LegalBoardCompositionEditor
                members={members}
                onMembersChange={(next) => { setMembers(next); setCompositionCorrection(false); setServerErrors([]); }}
                exceptionNote={exceptionNote}
                onExceptionChange={setExceptionNote}
                castellersInfo={castellersInfo}
                roleSuggestions={roleSuggestions}
                validation={validation}
                serverErrors={serverErrors}
                busy={busy}
            />

            {compositionChanged ? (
                <div className="legal-board-alert warning legal-board-correction-choice" role="group" aria-label="Persones o càrrecs modificats">
                    <p>Has modificat persones o càrrecs. És un canvi real de la junta o les dades estaven mal introduïdes?</p>
                    <button type="button" className="legal-board-button primary" disabled={busy} onClick={prepareChange}>És un canvi real: preparar el canvi de junta</button>
                    <label className="legal-board-checkbox">
                        <input type="checkbox" checked={compositionCorrection} disabled={busy} onChange={(event) => setCompositionCorrection(event.target.checked)} />
                        Les dades estaven mal introduïdes; la junta no ha canviat.
                    </label>
                    <p className="legal-board-small">Un canvi real crea un esborrany pendent de comunicar al Registre. Una correcció conserva l’estat de la junta actual.</p>
                </div>
            ) : null}
            <div className="legal-board-actions">
                <button type="button" className="legal-board-button primary" onClick={save} disabled={busy || (compositionChanged && !compositionCorrection)}>Desar la correcció</button>
                <button type="button" className="legal-board-button link" onClick={() => onClose()} disabled={busy}>Cancel·lar</button>
            </div>

            {typeof onAnnul === 'function' ? (
                <div className="legal-board-danger-zone">
                    <button type="button" className="legal-board-button link danger" onClick={onAnnul} disabled={busy}>Anul·lar aquest mandat</button>
                    <span className="legal-board-muted legal-board-small">
                        Si es va confirmar per error. Queda a l&apos;historial com a cancel·lat i, si n&apos;hi havia un d&apos;anterior, torna a ser el vigent.
                    </span>
                </div>
            ) : null}
        </section>
    );
}

export default LegalBoardCorrection;
